import { useEffect, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import {
  CheckCircle,
  AlertCircle,
  Loader2,
  ArrowRight,
  RefreshCw,
  XCircle,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import type { SyncHistoryEntry, EntitySyncState, SyncEntityType } from '@/types';
import { useSyncProgress } from '@/hooks/useSyncProgress';

interface SyncProgressIndicatorProps {
  /**
   * Entity type being synced (contacts, products, invoices)
   */
  entityType: SyncEntityType;
  /**
   * Static status to show when no sync is active
   */
  status: EntitySyncState | undefined;
  /**
   * Active sync history ID to track progress
   */
  activeSyncHistoryId: string | null;
  /**
   * Callback when sync completes
   */
  onSyncComplete?: (entry: SyncHistoryEntry) => void;
}

/**
 * Component that shows sync status with real-time progress during active syncs
 */
export function SyncProgressIndicator({
  entityType: _entityType, // Reserved for future use (e.g., entity-specific styling)
  status,
  activeSyncHistoryId,
  onSyncComplete,
}: SyncProgressIndicatorProps) {
  const [recentCompletion, setRecentCompletion] = useState<SyncHistoryEntry | null>(null);
  const [showCompletion, setShowCompletion] = useState(false);

  const { entry, isActive, statusMessage } = useSyncProgress({
    syncHistoryId: activeSyncHistoryId,
    enabled: !!activeSyncHistoryId,
    pollInterval: 1000, // Poll every second for responsive updates
    onComplete: (completed) => {
      setRecentCompletion(completed);
      setShowCompletion(true);
      onSyncComplete?.(completed);
      // Hide completion message after 5 seconds
      setTimeout(() => {
        setShowCompletion(false);
        setRecentCompletion(null);
      }, 5000);
    },
  });

  // Clear recent completion when a new sync starts
  useEffect(() => {
    if (activeSyncHistoryId && isActive) {
      setShowCompletion(false);
      setRecentCompletion(null);
    }
  }, [activeSyncHistoryId, isActive]);

  // Show active sync progress
  if (isActive && entry) {
    return <ActiveSyncProgress entry={entry} statusMessage={statusMessage} />;
  }

  // Show recent completion summary
  if (showCompletion && recentCompletion) {
    return <CompletionSummary entry={recentCompletion} />;
  }

  // Show static status when no sync is active
  return <StaticStatus status={status} />;
}

/**
 * Shows real-time progress during active sync
 */
function ActiveSyncProgress({
  entry,
  statusMessage,
}: {
  entry: SyncHistoryEntry;
  statusMessage: string;
}) {
  const entityLabel = entry.entityTypes
    .map((e) => e.charAt(0).toUpperCase() + e.slice(1))
    .join(', ');

  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
      <div className="flex items-center space-x-3">
        <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium text-blue-800">
              Running {entityLabel} Sync
            </p>
            <div className="flex items-center text-xs text-blue-600">
              <span>{entry.direction === 'to_xero' ? 'CRM' : 'Xero'}</span>
              <ArrowRight className="h-3 w-3 mx-1" />
              <span>{entry.direction === 'to_xero' ? 'Xero' : 'CRM'}</span>
            </div>
          </div>
          <p className="mt-1 text-xs text-blue-700 truncate">
            {statusMessage}
          </p>

          {/* Progress bar */}
          {entry.totalRecords > 0 && (
            <div className="mt-2">
              <div className="flex justify-between text-xs text-blue-600 mb-1">
                <span>
                  {entry.processedRecords} of {entry.totalRecords} records
                </span>
                <span>{entry.progressPercentage}%</span>
              </div>
              <div className="h-1.5 bg-blue-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-600 rounded-full transition-all duration-300"
                  style={{ width: `${entry.progressPercentage}%` }}
                />
              </div>
            </div>
          )}

          {/* Processing indicator when total unknown */}
          {entry.totalRecords === 0 && entry.processedRecords > 0 && (
            <div className="mt-2 flex items-center space-x-2">
              <RefreshCw className="h-3 w-3 text-blue-600 animate-spin" />
              <span className="text-xs text-blue-600">
                {entry.processedRecords} records processed...
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Shows completion summary after sync finishes
 */
function CompletionSummary({ entry }: { entry: SyncHistoryEntry }) {
  const isSuccess = entry.status === 'completed';
  const isPartial = entry.status === 'partial_success';
  const isFailed = entry.status === 'failed';

  const colorClasses = isSuccess
    ? {
        bg: 'bg-green-50',
        border: 'border-green-200',
        icon: 'text-green-600',
        title: 'text-green-800',
        text: 'text-green-700',
      }
    : isPartial
    ? {
        bg: 'bg-yellow-50',
        border: 'border-yellow-200',
        icon: 'text-yellow-600',
        title: 'text-yellow-800',
        text: 'text-yellow-700',
      }
    : {
        bg: 'bg-red-50',
        border: 'border-red-200',
        icon: 'text-red-600',
        title: 'text-red-800',
        text: 'text-red-700',
      };

  const Icon = isSuccess ? CheckCircle : isPartial ? AlertCircle : XCircle;

  // Build summary stats
  const stats = [];
  if (entry.createdRecords > 0) stats.push(`${entry.createdRecords} created`);
  if (entry.updatedRecords > 0) stats.push(`${entry.updatedRecords} updated`);
  if (entry.skippedRecords > 0) stats.push(`${entry.skippedRecords} skipped`);
  if (entry.failedRecords > 0) stats.push(`${entry.failedRecords} failed`);

  const summaryText = stats.length > 0 ? stats.join(', ') : `${entry.successfulRecords} synced`;

  return (
    <div className={cn('rounded-lg border p-4', colorClasses.bg, colorClasses.border)}>
      <div className="flex items-start space-x-3">
        <Icon className={cn('h-5 w-5 mt-0.5', colorClasses.icon)} />
        <div className="flex-1 min-w-0">
          <p className={cn('text-sm font-medium', colorClasses.title)}>
            {isSuccess ? 'Sync Complete' : isPartial ? 'Sync Partially Complete' : 'Sync Failed'}
          </p>
          <p className={cn('mt-1 text-xs', colorClasses.text)}>
            {summaryText}
          </p>
          {entry.durationSeconds && (
            <p className={cn('mt-1 text-xs', colorClasses.text)}>
              Completed in {formatDuration(entry.durationSeconds)}
            </p>
          )}
          {isFailed && entry.errorSummary && (
            <p className="mt-1 text-xs text-red-600 font-medium">
              {entry.errorSummary}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Shows static status when no sync is active
 */
function StaticStatus({ status }: { status: EntitySyncState | undefined }) {
  if (!status) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-5 w-5 text-gray-400 animate-pulse" />
          <div>
            <p className="text-sm font-medium text-gray-700">Loading status...</p>
          </div>
        </div>
      </div>
    );
  }

  const hasError = status.failedCount > 0 || !!status.lastError;
  const hasSynced = status.syncedCount > 0;
  const isEnabled = status.isEnabled;

  let statusColor = 'gray';
  let StatusIcon = XCircle;
  let statusText = 'Not Configured';

  if (!isEnabled) {
    statusColor = 'gray';
    StatusIcon = XCircle;
    statusText = 'Disabled';
  } else if (hasError) {
    statusColor = 'red';
    StatusIcon = AlertCircle;
    statusText = 'Sync Error';
  } else if (hasSynced) {
    statusColor = 'green';
    StatusIcon = CheckCircle;
    statusText = 'Synced';
  } else {
    statusColor = 'yellow';
    StatusIcon = Loader2;
    statusText = 'Pending';
  }

  const colorClasses = {
    gray: {
      bg: 'bg-gray-100',
      text: 'text-gray-700',
      icon: 'text-gray-400',
      badge: 'bg-gray-100 text-gray-600',
    },
    green: {
      bg: 'bg-green-50',
      text: 'text-green-900',
      icon: 'text-green-600',
      badge: 'bg-green-100 text-green-700',
    },
    yellow: {
      bg: 'bg-yellow-50',
      text: 'text-yellow-900',
      icon: 'text-yellow-600',
      badge: 'bg-yellow-100 text-yellow-700',
    },
    red: {
      bg: 'bg-red-50',
      text: 'text-red-900',
      icon: 'text-red-600',
      badge: 'bg-red-100 text-red-700',
    },
  };

  const colors = colorClasses[statusColor as keyof typeof colorClasses];

  return (
    <div className={cn('rounded-lg border', `border-${statusColor}-200`, colors.bg, 'p-4')}>
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-3 flex-1">
          <StatusIcon className={cn('h-5 w-5', colors.icon)} />
          <div className="flex-1">
            <div className="flex items-center space-x-2">
              <p className={cn('text-sm font-medium', colors.text)}>{statusText}</p>
              {status.syncedCount > 0 && (
                <span
                  className={cn(
                    'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                    colors.badge
                  )}
                >
                  {status.syncedCount} synced
                </span>
              )}
              {status.failedCount > 0 && (
                <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700">
                  {status.failedCount} failed
                </span>
              )}
            </div>
            {status.lastSyncAt && (
              <p className="mt-1 text-xs text-gray-600">
                Last synced{' '}
                {formatDistanceToNow(new Date(status.lastSyncAt), { addSuffix: true })}
              </p>
            )}
            {!status.lastSyncAt && isEnabled && (
              <p className="mt-1 text-xs text-gray-600">Never synced</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Format duration in seconds to human-readable string
 */
function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (remainingSeconds === 0) {
    return `${minutes}m`;
  }
  return `${minutes}m ${remainingSeconds}s`;
}
